import React, { useState, useEffect } from 'react';
import { 
  Clock, 
  Calendar, 
  AlertCircle, 
  CheckCircle, 
  XCircle,
  Filter,
  BarChart3,
  Users,
  Timer,
  Calendar as CalendarIcon,
  BookOpen,
  TrendingUp,
  Info,
  AlertTriangle
} from 'lucide-react';
import api from '../api';
import { toast } from 'react-toastify';

const EtudiantAssiduiteTab = ({ etudiant, etudiantId }) => {
  const [activeSubTab, setActiveSubTab] = useState('retards');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedYear, setSelectedYear] = useState('');

  useEffect(() => {
    fetchAssiduiteData();
  }, [etudiantId, selectedYear]);

  const fetchAssiduiteData = async () => {
    setLoading(true);
    try {
      const params = selectedYear ? `?annee=${selectedYear}` : '';
      const response = await api.get(`/etudiant/assiduite/${etudiantId}/${params}`);
      setData(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des données d\'assiduité:', error);
      toast.error('Erreur lors du chargement des données d\'assiduité');
    } finally {
      setLoading(false);
    }
  };

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}min`;
    }
    return `${mins}min`;
  };

  const subTabs = [
    {
      id: 'retards',
      label: 'Retards',
      icon: Clock,
      count: data?.retards?.length || 0,
      color: 'text-orange-600'
    },
    {
      id: 'absences',
      label: 'Absences',
      icon: XCircle,
      count: data?.absences?.length || 0,
      color: 'text-red-600'
    },
    {
      id: 'statistiques',
      label: 'Statistiques',
      icon: BarChart3,
      count: null,
      color: 'text-blue-600'
    }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202654]"></div>
        <span className="ml-3 text-gray-600">Chargement des données d'assiduité...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête avec filtre */}
      <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Assiduité & Ponctualité</h2>
            <p className="text-gray-600">Suivi des retards et absences</p>
          </div>
          <div className="flex items-center space-x-3">
            <Filter className="h-5 w-5 text-gray-400" />
            <select
              value={selectedYear}
              onChange={handleYearChange}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#202654] focus:border-transparent bg-white"
            >
              <option value="">Année en cours</option>
              {data?.annees_academiques?.map(annee => (
                <option key={annee.id} value={annee.id}>
                  {annee.libelle}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Statistiques rapides */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Clock className="h-5 w-5 text-orange-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-gray-600">Total Retards</p>
                <p className="text-xl font-semibold text-gray-900">{data?.statistiques?.total_retards || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-gray-600">Total Absences</p>
                <p className="text-xl font-semibold text-gray-900">{data?.statistiques?.total_absences || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Timer className="h-5 w-5 text-blue-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-gray-600">Temps Retard</p>
                <p className="text-xl font-semibold text-gray-900">
                  {formatTime(data?.statistiques?.temps_total_retard || 0)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <CalendarIcon className="h-5 w-5 text-purple-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-gray-600">Heures Absence</p>
                <p className="text-xl font-semibold text-gray-900">{data?.statistiques?.total_heures_absence || 0}h</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation sous-onglets */}
      <div className="bg-white border-b border-gray-200">
        <nav className="flex space-x-8 px-6">
          {subTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSubTab(tab.id)}
                className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                  activeSubTab === tab.id
                    ? 'border-[#202654] text-[#202654]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className={`h-4 w-4 mr-2 ${activeSubTab === tab.id ? 'text-[#202654]' : tab.color}`} />
                {tab.label}
                {tab.count !== null && (
                  <span className={`ml-2 py-1 px-2 rounded-full text-xs ${
                    activeSubTab === tab.id
                      ? 'bg-[#202654] text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Contenu des sous-onglets */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {activeSubTab === 'retards' && (
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Historique des Retards
            </h3>
            
            {data?.retards?.length > 0 ? (
              <div className="space-y-4">
                {data.retards.map((retard) => (
                  <div
                    key={retard.id}
                    className="bg-orange-50 border border-orange-200 rounded-lg p-4 hover:bg-orange-100 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="p-2 bg-orange-100 rounded-lg">
                          <Clock className="h-5 w-5 text-orange-600" />
                        </div>
                        <div className="ml-3">
                          <p className="font-medium text-gray-900">
                            {formatDate(retard.date_retard)}
                          </p>
                          <p className="text-sm text-gray-600">
                            {retard.matiere ? retard.matiere.libelle : 'Module non spécifié'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-orange-600">
                          {formatTime(retard.temps)}
                        </p>
                        <p className="text-sm text-gray-500">de retard</p>
                      </div>
                    </div>
                    {retard.motif && (
                      <div className="mt-3 p-3 bg-white rounded border border-orange-200">
                        <p className="text-sm text-gray-700">
                          <strong>Motif:</strong> {retard.motif}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Aucun retard enregistré
                </h3>
                <p className="text-gray-500">
                  Excellent ! L'étudiant n'a aucun retard pour cette période.
                </p>
              </div>
            )}
          </div>
        )}

        {activeSubTab === 'absences' && (
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Historique des Absences
            </h3>
            
            {data?.absences?.length > 0 ? (
              <div className="space-y-4">
                {data.absences.map((absence) => (
                  <div
                    key={absence.id}
                    className={`border rounded-lg p-4 ${
                      absence.absence 
                        ? 'bg-red-50 border-red-200 hover:bg-red-100' 
                        : 'bg-green-50 border-green-200 hover:bg-green-100'
                    } transition-colors`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`p-2 rounded-lg ${
                          absence.absence ? 'bg-red-100' : 'bg-green-100'
                        }`}>
                          {absence.absence ? (
                            <XCircle className="h-5 w-5 text-red-600" />
                          ) : (
                            <CheckCircle className="h-5 w-5 text-green-600" />
                          )}
                        </div>
                        <div className="ml-3">
                          <p className="font-medium text-gray-900">
                            {formatDate(absence.date_absence)}
                          </p>
                          <p className="text-sm text-gray-600">
                            {absence.matiere ? absence.matiere.libelle : 'Module non spécifié'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-lg font-semibold ${
                          absence.absence ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {absence.absence ? 'Absent' : 'Présent'}
                        </p>
                        <p className="text-sm text-gray-500">
                          {absence.volume_horaire}h de cours
                        </p>
                      </div>
                    </div>
                    {absence.motif && (
                      <div className={`mt-3 p-3 rounded border ${
                        absence.absence 
                          ? 'bg-white border-red-200' 
                          : 'bg-white border-green-200'
                      }`}>
                        <p className="text-sm text-gray-700">
                          <strong>Motif:</strong> {absence.motif}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Aucune absence enregistrée
                </h3>
                <p className="text-gray-500">
                  Parfait ! L'étudiant n'a aucune absence pour cette période.
                </p>
              </div>
            )}
          </div>
        )}

        {activeSubTab === 'statistiques' && (
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Statistiques Détaillées
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Statistiques Retards */}
              <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-lg p-6 border border-orange-200">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-orange-100 rounded-lg">
                    <Clock className="h-6 w-6 text-orange-600" />
                  </div>
                  <div className="ml-3">
                    <h4 className="text-lg font-semibold text-gray-900">Retards</h4>
                    <p className="text-sm text-gray-600">Année {data?.annee?.libelle}</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Nombre total</span>
                    <span className="text-xl font-semibold text-orange-600">
                      {data?.statistiques?.total_retards || 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Temps total</span>
                    <span className="text-xl font-semibold text-orange-600">
                      {formatTime(data?.statistiques?.temps_total_retard || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Moyenne par retard</span>
                    <span className="text-xl font-semibold text-orange-600">
                      {data?.statistiques?.total_retards > 0 
                        ? formatTime(Math.round(data?.statistiques?.temps_total_retard / data?.statistiques?.total_retards))
                        : '0min'
                      }
                    </span>
                  </div>
                </div>
              </div>

              {/* Statistiques Absences */}
              <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-lg p-6 border border-red-200">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-red-100 rounded-lg">
                    <XCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="ml-3">
                    <h4 className="text-lg font-semibold text-gray-900">Absences</h4>
                    <p className="text-sm text-gray-600">Année {data?.annee?.libelle}</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Nombre total</span>
                    <span className="text-xl font-semibold text-red-600">
                      {data?.statistiques?.total_absences || 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Heures perdues</span>
                    <span className="text-xl font-semibold text-red-600">
                      {data?.statistiques?.total_heures_absence || 0}h
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Moyenne par absence</span>
                    <span className="text-xl font-semibold text-red-600">
                      {data?.statistiques?.total_absences > 0 
                        ? Math.round(data?.statistiques?.total_heures_absence / data?.statistiques?.total_absences)
                        : '0'
                      }h
                    </span>
                  </div>
                </div>
              </div>

              {/* Évaluation globale */}
              <div className="md:col-span-2 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200">
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <TrendingUp className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-3">
                    <h4 className="text-lg font-semibold text-gray-900">Évaluation Globale</h4>
                    <p className="text-sm text-gray-600">Bilan de l'assiduité</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      (data?.statistiques?.total_retards || 0) === 0 
                        ? 'bg-green-100 text-green-800' 
                        : (data?.statistiques?.total_retards || 0) <= 5 
                          ? 'bg-yellow-100 text-yellow-800' 
                          : 'bg-red-100 text-red-800'
                    }`}>
                      {(data?.statistiques?.total_retards || 0) === 0 ? 'Excellent' : 
                       (data?.statistiques?.total_retards || 0) <= 5 ? 'Acceptable' : 'Préoccupant'}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">Ponctualité</p>
                  </div>
                  
                  <div className="text-center">
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      (data?.statistiques?.total_absences || 0) === 0 
                        ? 'bg-green-100 text-green-800' 
                        : (data?.statistiques?.total_absences || 0) <= 3 
                          ? 'bg-yellow-100 text-yellow-800' 
                          : 'bg-red-100 text-red-800'
                    }`}>
                      {(data?.statistiques?.total_absences || 0) === 0 ? 'Parfait' : 
                       (data?.statistiques?.total_absences || 0) <= 3 ? 'Correct' : 'Inquiétant'}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">Présence</p>
                  </div>
                  
                  <div className="text-center">
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                      (data?.statistiques?.total_retards || 0) === 0 && (data?.statistiques?.total_absences || 0) === 0
                        ? 'bg-green-100 text-green-800' 
                        : (data?.statistiques?.total_retards || 0) <= 5 && (data?.statistiques?.total_absences || 0) <= 3
                          ? 'bg-yellow-100 text-yellow-800' 
                          : 'bg-red-100 text-red-800'
                    }`}>
                      {(data?.statistiques?.total_retards || 0) === 0 && (data?.statistiques?.total_absences || 0) === 0 ? 'Exemplaire' : 
                       (data?.statistiques?.total_retards || 0) <= 5 && (data?.statistiques?.total_absences || 0) <= 3 ? 'Satisfaisant' : 'À améliorer'}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">Assiduité globale</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EtudiantAssiduiteTab; 